'use client';

import React from 'react';

import { PRIMARY } from './palette';

export interface ChartTextureProps {
  /** Unique per chart instance. Pass a `useId()` value, or a stable string. */
  id: string;
  /** Dot colour. Defaults to the single-series brand green. */
  color?: string;
  /** Distance between dot centres, in px. 4 is the house density. */
  spacing?: number;
  /** Dot radius, in px. Keep under spacing / 3 or the grid closes up. */
  radius?: number;
  /** Opacity of the dots at the top of the fill, before the fade. */
  opacity?: number;
  /** Soft tone painted under the dots so thin areas still read. */
  wash?: number;
}

export const textureFill = (id: string) => `url(#dv-dots-${id})`;
export const textureMask = (id: string) => `url(#dv-fade-${id})`;
export const textureWash = (id: string) => `url(#dv-wash-${id})`;

/**
 * Halftone fill for area and sparkline marks: a dot grid that thins out toward
 * the baseline. Drop it inside a Recharts `<defs>` and reference the ids with
 * the three helpers above.
 *
 * The dot grid is also secondary encoding — it survives greyscale print,
 * `forced-colors`, and full colour blindness, which is what earns it a place as
 * the house fill rather than decoration.
 */
export default function ChartTexture({
  id,
  color = PRIMARY,
  spacing = 5,
  radius = 0.95,
  opacity = 0.9,
  wash = 0.12,
}: ChartTextureProps) {
  return (
    <>
      <pattern
        id={`dv-dots-${id}`}
        width={spacing}
        height={spacing}
        patternUnits="userSpaceOnUse"
      >
        <circle
          cx={spacing / 2}
          cy={spacing / 2}
          r={radius}
          fill={color}
          fillOpacity={opacity}
        />
      </pattern>

      <linearGradient id={`dv-wash-${id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={wash} />
        <stop offset="80%" stopColor={color} stopOpacity={wash * 0.2} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>

      {/* Vertical fade applied to the dot grid: dense at the peak, gone at the
          baseline. A mask (not opacity) so the dots keep their own edges.
          `objectBoundingBox`, so the ramp maps to the AREA's own bbox rather
          than to the plot. Anchored to the plot (the previous behaviour) the
          texture disappeared on any chart whose data filled the plot: most of
          the fill then sat in the faded zone and the dots were invisible, which
          is exactly how the spend-trend chart shipped looking untextured. */}
      {/* Two stops only, so the ramp is LINEAR: an eased 3-stop version held
          most of its density through the upper half and then dropped, which
          reads as a band with a soft edge rather than as a fade. */}
      <linearGradient id={`dv-fade-grad-${id}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity={1} />
        <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
      </linearGradient>
      <mask id={`dv-fade-${id}`} maskContentUnits="objectBoundingBox">
        <rect
          x="0"
          y="0"
          width="1"
          height="1"
          fill={`url(#dv-fade-grad-${id})`}
        />
      </mask>
    </>
  );
}

// ---------------------------------------------------------------------------
// Multi-series texture — identity from TEXTURE, not from hue
// ---------------------------------------------------------------------------

/**
 * Stacked/multi-series fills built from ONE hue, where each series is told apart
 * by the WEIGHT of its halftone rather than by its colour.
 *
 * Why this exists: the categorical `SERIES` palette is tuned for colourblind
 * separation (worst adjacent pair deuteranopia dE 18.2), which makes every slot
 * maximally UNLIKE its neighbours — green, then violet, then orange, then cyan.
 * That is the right objective for a legend and the wrong one for a six-band
 * stacked area, where the bands touch: six saturated competing hues on a
 * near-black surface read as noise no matter which six you pick.
 *
 * So identity moves to the texture. Density is ordered by MAGNITUDE, densest
 * first, which makes the stack self-ranking: the widest band is the most solid,
 * the tail is the sparsest. Two redundant channels carry it (dot density and a
 * slight value step), so it survives greyscale, `forced-colors` and full colour
 * blindness — the same reason the single-series halftone earns its place.
 *
 * Each band ALSO fades toward its own baseline, via `seriesTextureMask`. The
 * fade is per-band and expressed in `objectBoundingBox` units, so it maps to
 * that band's own bbox rather than to the plot: a plot-anchored fade (what
 * `ChartTexture` uses for a single series) would erase whichever bands happen
 * to sit low in a stack. A constant-density grid was the first attempt here and
 * read as flat and mechanical -- the halftone has to thin out or it is just a
 * screen door.
 */
export type SeriesTextureVariant = 'density' | 'geometry';

export interface SeriesTextureProps {
  /** Unique per chart instance. Pass a `useId()` value. */
  id: string;
  /** How many series to emit fills for. */
  count: number;
  /** The single hue every band shares. Ignored when `colors` is passed. */
  color?: string;
  /**
   * Per-series hues, when identity should come from BOTH colour and texture.
   * Pass the `SERIES` slots here for a stacked chart; omit it for the one-hue
   * treatment where texture alone separates the bands.
   */
  colors?: string[];
  /** `density` grades one dot grid; `geometry` also changes the mark shape. */
  variant?: SeriesTextureVariant;
}

/** Fill for series `i` (0 = largest / densest). */
export const seriesTextureFill = (id: string, i: number) =>
  `url(#dv-st-${id}-${i})`;

/**
 * Per-band fade mask for series `i`. Apply alongside `seriesTextureFill` so the
 * dots thin toward that band's own baseline instead of holding one density.
 */
export const seriesTextureMask = (id: string, i: number) =>
  `url(#dv-stf-${id}-${i})`;

/**
 * Top-edge stroke for series `i`. A stacked band needs its own edge because the
 * fills deliberately share a hue; without it, adjacent bands merge.
 */
export function seriesEdge(i: number, count: number) {
  const t = count <= 1 ? 0 : i / (count - 1);
  return { strokeOpacity: 0.95 - t * 0.5, strokeWidth: 1.25 };
}

/**
 * Dot grid geometry for series `i`, densest first.
 *
 * Exported so a LEGEND can build a swatch from the same numbers the band uses.
 * When the hue is shared, a plain colour chip tells the reader nothing: all six
 * chips look identical and the legend stops being a key. The swatch has to carry
 * the texture.
 */
export function seriesDensity(i: number, count: number) {
  const t = count <= 1 ? 0 : i / (count - 1);
  return {
    // 3px (nearly closed) out to 11px (hairline scatter).
    spacing: 3 + t * 8,
    // Radius shrinks with spacing, but slower, so the grid thins rather than
    // just spreading out.
    radius: 1.15 - t * 0.35,
    // The redundant value channel: the tail is quieter as well as sparser.
    opacity: 0.95 - t * 0.35,
  };
}

export function SeriesTexture({
  id,
  count,
  color = PRIMARY,
  colors,
  variant = 'density',
}: SeriesTextureProps) {
  return (
    <>
      {/* One fade per band. `maskContentUnits="objectBoundingBox"` makes the
          0..1 rect cover the masked path's own bbox, and the gradient inside it
          is objectBoundingBox by default, so the ramp is relative to the band
          rather than to the chart. */}
      {Array.from({ length: count }, (_, i) => (
        <React.Fragment key={`fade-${i}`}>
          {/* Linear, two stops. See the single-series note above. */}
          <linearGradient
            id={`dv-stfg-${id}-${i}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
          </linearGradient>
          <mask
            id={`dv-stf-${id}-${i}`}
            maskContentUnits="objectBoundingBox"
          >
            <rect
              x="0"
              y="0"
              width="1"
              height="1"
              fill={`url(#dv-stfg-${id}-${i})`}
            />
          </mask>
        </React.Fragment>
      ))}

      {Array.from({ length: count }, (_, i) => {
        const { spacing, radius, opacity } = seriesDensity(i, count);
        const paint = colors?.[i] ?? color;
        const pid = `dv-st-${id}-${i}`;
        // Geometry variant swaps the mark every other slot so shape carries
        // some of the load; density still grades underneath it.
        const asLines = variant === 'geometry' && i % 2 === 1;
        return (
          <pattern
            key={i}
            id={pid}
            width={spacing}
            height={spacing}
            patternUnits="userSpaceOnUse"
            patternTransform={asLines ? 'rotate(45)' : undefined}
          >
            {asLines ? (
              <rect
                x={0}
                y={0}
                width={radius * 1.4}
                height={spacing}
                fill={paint}
                fillOpacity={opacity}
              />
            ) : (
              <>
                {/* Wash under the dots so a thin band still reads as a band
                    rather than as scattered specks.
                    Sized against what it REPLACED: the old fill was a 0.55 ->
                    0.12 gradient, and a dot grid alone only inks 2-15% of its
                    tile, so a token wash left the whole chart near-invisible.
                    Graded per series so the stack keeps its depth order. */}
                <rect
                  x={0}
                  y={0}
                  width={spacing}
                  height={spacing}
                  fill={paint}
                  fillOpacity={0.34 - (i / Math.max(1, count - 1)) * 0.16}
                />
                <circle
                  cx={spacing / 2}
                  cy={spacing / 2}
                  r={radius}
                  fill={paint}
                  fillOpacity={opacity}
                />
              </>
            )}
          </pattern>
        );
      })}
    </>
  );
}
